import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import { useAuth as useClerkAuth } from '@clerk/clerk-react';
import { useAuth } from '@/context/AuthContext';
import { apiClient } from '@/api/axios';
import { getStoredBusinessDetails } from '@/hooks/useProBusinessDetails';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Business {
  id: string;
  ownerUserId: string;
  name: string;
  businessTypes: string[];
  status: string;
  logoUrl?: string;
  email?: string;
  phone?: string;
  website?: string;
  description?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  openingHours?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface BusinessMembership {
  business: Business;
  role: string;
  memberId: string;
}

const ACTIVE_BUSINESS_KEY = 'petso_pro_active_business';

interface BusinessContextType {
  // Multi-business state
  memberships: BusinessMembership[];
  activeBusinessId: string | null;
  needsBusinessSelection: boolean;
  isMultiBusiness: boolean;

  // Backward-compatible computed properties
  business: Business | null;
  businessId: string | null;
  role: string | null;
  hasBusiness: boolean;

  // Loading / error
  isLoading: boolean;
  isSettingUp: boolean;
  error: string | null;

  // Actions
  setActiveBusiness: (businessId: string) => void;
  refetch: () => Promise<void>;
}

const BusinessContext = createContext<BusinessContextType | undefined>(undefined);

// ─── Provider ────────────────────────────────────────────────────────────────

export function BusinessProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded: isClerkLoaded, getToken } = useClerkAuth();
  const { user } = useAuth();

  const [memberships, setMemberships] = useState<BusinessMembership[]>([]);
  const [activeBusinessId, setActiveBusinessId] = useState<string | null>(null);
  const [needsBusinessSelection, setNeedsBusinessSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasAttemptedAutoSetup = useRef(false);

  // Derived state from active membership
  const activeMembership = memberships.find(
    (m) => m.business.id === activeBusinessId
  ) ?? null;
  const business = activeMembership?.business ?? null;
  const role = activeMembership?.role ?? null;
  const hasBusiness = !!business;
  const isMultiBusiness = memberships.length > 1;

  // Helper: make an authenticated API call using Clerk's getToken directly.
  const authGet = useCallback(
    async <T,>(url: string): Promise<T> => {
      let token: string | null = null;
      try {
        token = await getToken();
      } catch {
        throw new Error('NO_TOKEN');
      }
      if (!token) throw new Error('NO_TOKEN');
      const { data } = await apiClient.get<T>(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    },
    [getToken]
  );

  const authPost = useCallback(
    async <T,>(url: string, body: unknown): Promise<T> => {
      let token: string | null = null;
      try {
        token = await getToken();
      } catch {
        throw new Error('NO_TOKEN');
      }
      if (!token) throw new Error('NO_TOKEN');
      const { data } = await apiClient.post<T>(url, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    },
    [getToken]
  );

  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 4;

  // Resolve active business from memberships
  const resolveActiveBusiness = useCallback(
    (newMemberships: BusinessMembership[]) => {
      if (newMemberships.length === 0) {
        setActiveBusinessId(null);
        setNeedsBusinessSelection(false);
        return;
      }

      // 1. Check localStorage for stored selection
      const storedId = localStorage.getItem(ACTIVE_BUSINESS_KEY);
      if (storedId && newMemberships.some((m) => m.business.id === storedId)) {
        setActiveBusinessId(storedId);
        setNeedsBusinessSelection(false);
        return;
      }

      // 2. If exactly one membership, auto-select
      if (newMemberships.length === 1) {
        const id = newMemberships[0].business.id;
        setActiveBusinessId(id);
        localStorage.setItem(ACTIVE_BUSINESS_KEY, id);
        setNeedsBusinessSelection(false);
        return;
      }

      // 3. Multiple memberships and no valid stored ID → prompt user
      setActiveBusinessId(null);
      setNeedsBusinessSelection(true);
    },
    []
  );

  const fetchBusiness = useCallback(async () => {
    if (!isClerkLoaded) return;

    if (!isSignedIn) {
      setMemberships([]);
      setActiveBusinessId(null);
      setNeedsBusinessSelection(false);
      setIsLoading(false);
      retryCountRef.current = 0;
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await authGet<{
        businesses?: BusinessMembership[];
        business: Business | null;
        role: string | null;
        memberId?: string;
      }>('/pro/businesses/mine');

      // Build memberships array — prefer data.businesses, fall back to wrapping single
      let newMemberships: BusinessMembership[];
      if (data.businesses && Array.isArray(data.businesses) && data.businesses.length > 0) {
        newMemberships = data.businesses;
      } else if (data.business) {
        newMemberships = [
          {
            business: data.business,
            role: data.role ?? 'owner',
            memberId: data.memberId ?? '',
          },
        ];
      } else {
        newMemberships = [];
      }

      setMemberships(newMemberships);
      resolveActiveBusiness(newMemberships);
      retryCountRef.current = 0;
    } catch (err: any) {
      if (retryCountRef.current < MAX_RETRIES) {
        const delay = err?.message === 'NO_TOKEN' ? 500 : 1000 * (retryCountRef.current + 1);
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => fetchBusiness(), delay);
        return;
      }
      console.error('Failed to fetch business:', err);
      setError(err?.response?.data?.error || 'Failed to load business');
      setMemberships([]);
      setActiveBusinessId(null);
    } finally {
      if (retryCountRef.current === 0 || retryCountRef.current >= MAX_RETRIES) {
        setIsLoading(false);
      }
    }
  }, [isSignedIn, isClerkLoaded, authGet, resolveActiveBusiness]);

  useEffect(() => {
    fetchBusiness();
  }, [fetchBusiness]);

  // ── setActiveBusiness ─────────────────────────────────────────────────────
  const setActiveBusiness = useCallback(
    (businessId: string) => {
      const exists = memberships.some((m) => m.business.id === businessId);
      if (!exists) return;
      localStorage.setItem(ACTIVE_BUSINESS_KEY, businessId);
      setActiveBusinessId(businessId);
      setNeedsBusinessSelection(false);
    },
    [memberships]
  );

  // ── Auto-provision business when user is signed in but has NO memberships ──
  useEffect(() => {
    if (isLoading || isSettingUp || hasAttemptedAutoSetup.current) return;
    if (!isSignedIn || !user) return;
    // Only auto-create when there are zero memberships
    if (memberships.length > 0) return;

    const autoSetup = async () => {
      hasAttemptedAutoSetup.current = true;
      setIsSettingUp(true);

      try {
        // Check if user has any pending team invitations
        try {
          const membershipCheck = await authGet<{ membership: any }>('/pro/me/membership');
          if (membershipCheck?.membership) {
            await fetchBusiness();
            return;
          }
        } catch {
          // 404 means no membership — proceed
        }

        try {
          const inviteCheck = await authGet<{ hasPendingInvite: boolean }>('/pro/me/pending-invite');
          if (inviteCheck?.hasPendingInvite) {
            return;
          }
        } catch {
          // Endpoint might not exist yet
        }

        // No invitations found — auto-provision
        const stored = getStoredBusinessDetails(user.clerkId);

        await authPost('/pro/businesses', {
          name:
            stored?.businessName ||
            user.businessName ||
            user.name ||
            'My Business',
          businessTypes: stored?.businessType || [],
          email: stored?.email || user.email || undefined,
          phone: stored?.phone || undefined,
          website: stored?.website || undefined,
          description: stored?.description || undefined,
          address: stored?.address || undefined,
          openingHours: stored?.openingHours || undefined,
        });

        await fetchBusiness();
      } catch (err: any) {
        console.error('Failed to auto-setup business:', err);
        if (err?.response?.status === 409) {
          await fetchBusiness();
        } else {
          setError('Failed to set up your business workspace');
        }
      } finally {
        setIsSettingUp(false);
      }
    };

    autoSetup();
  }, [isLoading, isSettingUp, isSignedIn, user, memberships.length, fetchBusiness, authGet, authPost]);

  // Reset auto-setup guard on sign-out and clear stored active business
  useEffect(() => {
    if (!isSignedIn) {
      hasAttemptedAutoSetup.current = false;
      localStorage.removeItem(ACTIVE_BUSINESS_KEY);
    }
  }, [isSignedIn]);

  // Clean up retry timer on unmount
  useEffect(() => {
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, []);

  const value: BusinessContextType = {
    memberships,
    activeBusinessId,
    needsBusinessSelection,
    isMultiBusiness,
    business,
    businessId: activeBusinessId,
    role,
    hasBusiness,
    isLoading: isLoading || isSettingUp,
    isSettingUp,
    error,
    setActiveBusiness,
    refetch: fetchBusiness,
  };

  return (
    <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useBusiness(): BusinessContextType {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}
